import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { vehicleService } from '../../../services/vehicleService';
import { driverService } from '../../../services/driverService';

const PricingCalculator = ({ isOpen, onClose }) => {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [vehiclesData, driversData] = await Promise.all([
        vehicleService?.getAvailable(),
        driverService?.getAvailable()
      ]);
      setVehicles(vehiclesData);
      setDrivers(driversData);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateEstimate = () => {
    if (!selectedVehicleId || !startDate || !endDate) {
      return null;
    }

    const vehicle = vehicles?.find(v => v?.id === selectedVehicleId);
    const driver = selectedDriverId ? drivers?.find(d => d?.id === selectedDriverId) : null;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const vehicleCost = (vehicle?.dailyRate || 0) * totalDays;
    const driverCost = driver ? (driver?.dailyRate || 0) * totalDays : 0;
    const totalCost = vehicleCost + driverCost;

    return {
      vehicle,
      driver,
      totalDays,
      vehicleCost,
      driverCost,
      totalCost
    };
  };

  const estimate = calculateEstimate();

  const vehicleOptions = [
    { value: '', label: 'Sélectionnez un véhicule' },
    ...vehicles?.map(v => ({
      value: v?.id,
      label: `${v?.name} - ${v?.dailyRate?.toLocaleString()} CDF/jour`,
      description: `${v?.vehicleType} • ${v?.seats} places`
    }))
  ];

  const driverOptions = [
    { value: '', label: 'Sans chauffeur (conduire moi-même)' },
    ...drivers?.map(d => ({
      value: d?.id,
      label: `${d?.fullName} - ${d?.dailyRate?.toLocaleString()} CDF/jour`,
      description: `${d?.experienceYears} ans d'exp. • Note: ${d?.rating}/5`
    }))
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Icon name="Calculator" size={24} className="text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Calculateur de Prix</h2>
              <p className="text-sm text-slate-400 mt-1">
                Estimez le coût de votre location
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-blue-500 border-t-transparent" />
              <p className="mt-4 text-slate-400">Chargement...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Vehicle Selection */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  <Icon name="Car" size={16} className="inline mr-2" />
                  Véhicule
                </label>
                <Select
                  options={vehicleOptions}
                  value={selectedVehicleId}
                  onChange={setSelectedVehicleId}
                  placeholder="Choisissez un véhicule"
                  searchable
                />
              </div>

              {/* Driver Selection */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  <Icon name="User" size={16} className="inline mr-2" />
                  Chauffeur (Optionnel)
                </label>
                <Select
                  options={driverOptions}
                  value={selectedDriverId}
                  onChange={setSelectedDriverId}
                  placeholder="Avec ou sans chauffeur"
                  searchable
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="date"
                  label="Date de début"
                  value={startDate}
                  onChange={(e) => setStartDate(e?.target?.value)}
                  min={new Date()?.toISOString()?.split('T')?.[0]}
                />
                <Input
                  type="date"
                  label="Date de fin"
                  value={endDate}
                  onChange={(e) => setEndDate(e?.target?.value)}
                  min={startDate || new Date()?.toISOString()?.split('T')?.[0]}
                />
              </div>

              {/* Results */}
              {estimate && (
                <div className="bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-amber-500/10 rounded-xl p-6 border border-blue-500/20">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Icon name="Receipt" size={20} />
                    Estimation du Coût
                  </h3>

                  <div className="space-y-4">
                    {/* Duration */}
                    <div className="flex items-center justify-between pb-3 border-b border-white/10">
                      <div className="flex items-center gap-2">
                        <Icon name="Calendar" size={16} className="text-slate-400" />
                        <span className="text-sm text-slate-400">Durée de location</span>
                      </div>
                      <span className="font-semibold text-white">
                        {estimate?.totalDays} jour{estimate?.totalDays > 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Vehicle Cost */}
                    <div className="flex items-center justify-between pb-3 border-b border-white/10">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon name="Car" size={16} className="text-slate-400" />
                          <span className="text-sm text-slate-400">Véhicule</span>
                        </div>
                        <p className="text-xs text-slate-400 ml-6">{estimate?.vehicle?.name}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-white">
                          {estimate?.vehicleCost?.toLocaleString()} CDF
                        </p>
                        <p className="text-xs text-slate-400">
                          {estimate?.vehicle?.dailyRate?.toLocaleString()} CDF × {estimate?.totalDays}
                        </p>
                      </div>
                    </div>

                    {/* Driver Cost */}
                    {estimate?.driver && (
                      <div className="flex items-center justify-between pb-3 border-b border-white/10">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Icon name="User" size={16} className="text-slate-400" />
                            <span className="text-sm text-slate-400">Chauffeur</span>
                          </div>
                          <p className="text-xs text-slate-400 ml-6">{estimate?.driver?.fullName}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-white">
                            {estimate?.driverCost?.toLocaleString()} CDF
                          </p>
                          <p className="text-xs text-slate-400">
                            {estimate?.driver?.dailyRate?.toLocaleString()} CDF × {estimate?.totalDays}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Total */}
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-lg font-bold text-white">Coût Total</span>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-blue-400">
                          {estimate?.totalCost?.toLocaleString()} CDF
                        </p>
                        <p className="text-sm text-slate-400">
                          ≈ ${(estimate?.totalCost / 1000)?.toFixed(0)} USD
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Info Note */}
                  <div className="mt-6 p-4 bg-slate-900/50 rounded-lg border border-white/10">
                    <div className="flex gap-3">
                      <Icon name="Info" size={16} className="text-blue-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-slate-400 space-y-1">
                        <p>• Prix indicatif, peut varier selon disponibilité</p>
                        <p>• Carburant non inclus dans le tarif</p>
                        <p>• Caution requise à la prise en charge</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!estimate && (
                <div className="text-center py-12 bg-slate-800/30 rounded-xl border-2 border-dashed border-white/10">
                  <Icon name="Calculator" size={48} className="text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-400">
                    Sélectionnez un véhicule et les dates pour voir l'estimation
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/10 bg-slate-800/30">
          <div className="flex gap-3">
            <Button variant="outline" fullWidth onClick={onClose}>
              Fermer
            </Button>
            {estimate && (
              <Button
                variant="default"
                fullWidth
                iconName="Calendar"
                iconPosition="left"
                onClick={() => {
                  onClose();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                Réserver Maintenant
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;
